// src/features/streams/components/StreamsPage.tsx

import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Stack, Title, Text, Loader, Alert, Group } from '@mantine/core';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { IconAlertCircle } from '@tabler/icons-react';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useSharedLinkConfig } from '@/features/link/useLinkConfig';
import { useStreams } from '../useStreams';
import { useStreamLiveStatus } from '../useStreamLiveStatus';
import {
  useActivateStreams,
  useBatchUpdateStreamTags,
  useDeactivateStreams,
  useDeleteStreams,
} from '../useStreamMutations';
import { useTags } from '@/features/tag/useTags';
import { BatchTagModal } from '@/shared/components/BatchTagModal';
import type { BatchTagItem, BatchTagMode, BatchTagPlan } from '@/features/tag/TagBatchUtils';
import { useStreamGridEngine } from '../hooks/useStreamGridEngine';
import { useStreamGridState } from '../stores/streamGridState';
import { useSelection } from '@/shared/contexts/SelectionContext';
import { PublishModal } from '@/shared/components/PublishModal';
import { StreamGrid } from './StreamGrid';
import { StreamToolbar } from './StreamToolbar';
import { StreamsViewSwitch } from './StreamsViewSwitch';
import { StreamEncoderSetupModal } from './StreamEncoderSetupModal';
import { CreateStreamModal } from './CreateStreamModal';
import { SearchDebounce } from '@/shared/utils/searchDebounce';
import { markEnd, markStart } from '@/shared/utils/perf';
import type { StreamViewData, StreamGridRow, StreamSortKey } from '@/lib/types/Stream';
import type { IBackendLiveData } from '@/lib/types/Customer';

function StreamsPageContent() {
  const { selectedCId, customerInfo, selectedCustomer } = useAuth();
  const navigate = useNavigate();
  const { streams, isLoading: streamsLoading, isError, error } = useStreams();
  const { data: linkConfig } = useSharedLinkConfig();
  const { liveStatusMap } = useStreamLiveStatus();
  const { selectedIds, clearSelection } = useSelection();

  const searchText = useStreamGridState((s) => s.searchText);
  const setSearchText = useStreamGridState((s) => s.setSearchText);
  const searchMode = useStreamGridState((s) => s.searchMode);
  const setSearchMode = useStreamGridState((s) => s.setSearchMode);
  const sortKey = useStreamGridState((s) => s.sortKey);
  const sortDirection = useStreamGridState((s) => s.sortDirection);
  const setSort = useStreamGridState((s) => s.setSort);
  const setScrollTop = useStreamGridState((s) => s.setScrollTop);
  const awaitingBackNavPaint = useStreamGridState((s) => s.awaitingBackNavPaint);
  const completeBackNavPaint = useStreamGridState((s) => s.completeBackNavPaint);

  const activateMutation = useActivateStreams();
  const deactivateMutation = useDeactivateStreams();
  const deleteMutation = useDeleteStreams();
  const batchTagMutation = useBatchUpdateStreamTags();
  const { tags: allTags } = useTags();

  const [batchTagOpen, setBatchTagOpen] = useState(false);
  const [publishStream, setPublishStream] = useState<StreamViewData | null>(null);
  const [encoderStream, setEncoderStream] = useState<StreamViewData | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const initialScrollTopRef = useRef(useStreamGridState.getState().scrollTop);
  const hasReportedFirstRowsPaintRef = useRef(false);
  const pendingSortPaintRef = useRef(false);

  const liveBackends = customerInfo?.liveBackends ?? [];
  const quotaFeatures = selectedCustomer?.cfg.quota.features;
  const quotaLimits = selectedCustomer?.cfg.quota.limits;

  // Check stream creation entitlement/limits
  const allowStream = quotaFeatures?.account.allowStream ?? false;
  const allowDvr = quotaFeatures?.account.allowDvr ?? false;
  const allowEncryptLive = quotaFeatures?.account.allowEncryptLive ?? false;
  const videostreamLimit = quotaLimits?.videostreamLimit ?? null;

  // Count non-deleted streams for limit comparison
  const activeStreamCount = useMemo(
    () => streams.filter(s => s.media.mediaStatusType !== MediaStatusType.Deleted).length,
    [streams],
  );

  const isAtStreamLimit = videostreamLimit !== null && activeStreamCount >= videostreamLimit;

  const createDisabledReason = isAtStreamLimit
    ? `Stream limit reached (${activeStreamCount}/${videostreamLimit})`
    : undefined;

  // Route guard: redirect to /videos if stream entitlement is missing
  useEffect(() => {
    if (selectedCustomer && !allowStream) {
      navigate({ to: '/videos' });
    }
  }, [selectedCustomer, allowStream, navigate]);

  // Build backend map for encoder setup
  const backendMap = useMemo(() => {
    const map = new Map<number, IBackendLiveData>();
    for (const b of liveBackends) {
      map.set(b.backendId, b);
    }
    return map;
  }, [liveBackends]);

  useEffect(() => {
    markStart('streams.grid.firstRowsPaint');
  }, []);

  // Escape key clears selection
  useHotkeys([
    ['Escape', () => clearSelection()],
  ]);

  const [debouncedSearchText] = useDebouncedValue(searchText, SearchDebounce.forItemCount(streams.length));

  const gridEngine = useStreamGridEngine({
    customerId: selectedCId,
    streams,
    sort: { key: sortKey, direction: sortDirection },
    search: { mode: searchMode, text: debouncedSearchText },
    liveStatusMap,
    liveBackends,
    linkConfig: linkConfig ?? null,
  });


  // Selections survive search changes; surface how many selected rows the
  // current search is hiding so the count never looks "wrong".
  const hiddenSelectedCount = useMemo(() => {
    if (selectedIds.size === 0) return 0;
    const visible = new Set(gridEngine.orderedIds);
    let hidden = 0;
    for (const id of selectedIds) {
      if (!visible.has(id)) hidden++;
    }
    return hidden;
  }, [selectedIds, gridEngine.orderedIds]);

  const streamsById = useMemo(
    () => new Map(streams.map((s) => [s.media.mediaId, s])),
    [streams],
  );

  const handleSort = useCallback(
    (key: StreamSortKey) => {
      pendingSortPaintRef.current = true;
      markStart('streams.grid.sort.total');
      setSort(key);
    },
    [setSort],
  );

  // --- Bulk action handlers ---

  const handleBulkActivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    activateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Activated', message: `${ids.length} stream(s) activated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, activateMutation, clearSelection]);

  const handleBulkDeactivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    deactivateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Deactivated', message: `${ids.length} stream(s) deactivated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, deactivateMutation, clearSelection]);

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    modals.openConfirmModal({
      title: 'Delete Streams',
      children: (
        <Text size="sm">
          Are you sure you want to delete {ids.length} stream(s)? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate(ids, {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `${ids.length} stream(s) deleted.`, color: 'green' });
            clearSelection();
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [selectedIds, deleteMutation, clearSelection]);

  // --- Batch tags handlers (same shape as videos) ---

  // Live view of the selection for the modal (selection can't change while a
  // modal is open; list refetches keep the plan preview current).
  const batchTagItems = useMemo<BatchTagItem[]>(() => {
    if (!batchTagOpen) return [];
    const items: BatchTagItem[] = [];
    for (const id of selectedIds) {
      const stream = streamsById.get(id);
      if (stream) {
        items.push({ id, title: stream.media.title, tags: stream.tags ?? [] });
      }
    }
    return items;
  }, [batchTagOpen, selectedIds, streamsById]);

  const handleBatchTagApply = useCallback((plan: BatchTagPlan, mode: BatchTagMode, tags: string[]) => {
    // Send only the ids the plan says will actually change — the server does
    // the set math (add = ensure present, remove = ensure absent), so a
    // retry after an error is always safe.
    batchTagMutation.mutate(
      {
        mediaIds: plan.changed.map((entry) => entry.id),
        addTags: mode === 'add' ? tags : [],
        removeTags: mode === 'remove' ? tags : [],
      },
      {
        onSuccess: (result) => {
          setBatchTagOpen(false);
          clearSelection();
          const verb = mode === 'add' ? 'added to' : 'removed from';
          const tagLabel = tags.length === 1 ? `Tag "${tags[0]}"` : `${tags.length} tags`;
          notifications.show({
            title: mode === 'add' ? 'Tags Added' : 'Tags Removed',
            message: `${tagLabel} ${verb} ${result.updatedMediaCount} stream(s).${
              plan.overLimit.length > 0 ? ` ${plan.overLimit.length} skipped (tag limit).` : ''
            }`,
            color: 'green',
          });
        },
        onError: (err) => {
          // Keep the modal open — applying again is idempotent server-side.
          notifications.show({
            title: 'Error',
            message: `${err.message} — you can apply again to retry.`,
            color: 'red',
            autoClose: 8000,
          });
        },
      },
    );
  }, [batchTagMutation, clearSelection]);

  // --- Single-row action handlers ---

  const handlePublish = useCallback((gridRow: StreamGridRow) => {
    const fullStream = streamsById.get(gridRow.mediaId);
    if (fullStream) setPublishStream(fullStream);
  }, [streamsById]);

  const handleEncoderSetup = useCallback((gridRow: StreamGridRow) => {
    const fullStream = streamsById.get(gridRow.mediaId);
    if (fullStream) setEncoderStream(fullStream);
  }, [streamsById]);

  const handleRowActivateToggle = useCallback((stream: StreamGridRow) => {
    const mutation = stream.isActive ? deactivateMutation : activateMutation;
    const action = stream.isActive ? 'Deactivated' : 'Activated';
    mutation.mutate([stream.mediaId], {
      onSuccess: () => {
        notifications.show({ title: action, message: `"${stream.title}" ${action.toLowerCase()}.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [activateMutation, deactivateMutation]);

  const handleRowDelete = useCallback((stream: StreamGridRow) => {
    deleteMutation.mutate([stream.mediaId], {
      onSuccess: () => {
        notifications.show({ title: 'Deleted', message: `"${stream.title}" deleted.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [deleteMutation]);

  const handleFirstRowsPaint = useCallback(() => {
    if (!hasReportedFirstRowsPaintRef.current) {
      hasReportedFirstRowsPaintRef.current = true;
      markEnd('streams.grid.firstRowsPaint', {
        count: gridEngine.count,
      });
    }

    if (pendingSortPaintRef.current) {
      pendingSortPaintRef.current = false;
      markEnd('streams.grid.sort.total', {
        count: gridEngine.count,
        key: sortKey,
        direction: sortDirection,
      });
    }

    if (!awaitingBackNavPaint) return;
    markEnd('streams.grid.backnav.firstRowsPaint', {
      count: gridEngine.count,
    });
    completeBackNavPaint();
  }, [
    awaitingBackNavPaint,
    completeBackNavPaint,
    gridEngine.count,
    sortDirection,
    sortKey,
  ]);

  if (streamsLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading streams...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load streams'}
      </Alert>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ flex: 1, minHeight: 0 }}>
      <Group justify="space-between" align="center">
        <Title order={2}>Streams</Title>
        <StreamsViewSwitch active="account" />
      </Group>

      <StreamToolbar
        searchText={searchText}
        onSearchChange={setSearchText}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        totalCount={streams.length}
        resultCount={gridEngine.resultCount}
        hiddenSelectedCount={hiddenSelectedCount}
        onBulkActivate={handleBulkActivate}
        onBulkDeactivate={handleBulkDeactivate}
        onBatchTags={() => setBatchTagOpen(true)}
        onBulkDelete={handleBulkDelete}
        onCreateStream={() => setCreateModalOpen(true)}
        activateLoading={activateMutation.isPending}
        deactivateLoading={deactivateMutation.isPending}
        deleteLoading={deleteMutation.isPending}
        createDisabled={isAtStreamLimit}
        createDisabledReason={createDisabledReason}
      />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <StreamGrid
          count={gridEngine.count}
          allIds={gridEngine.orderedIds}
          getRow={gridEngine.getRowByIndex}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onPublish={handlePublish}
          onEncoderSetup={handleEncoderSetup}
          onActivateToggle={handleRowActivateToggle}
          onDelete={handleRowDelete}
          initialScrollTop={initialScrollTopRef.current}
          onScrollTopChange={setScrollTop}
          onFirstVisibleRowsPaint={handleFirstRowsPaint}
        />
      </div>

      {batchTagOpen && (
        <BatchTagModal
          opened={batchTagOpen}
          onClose={() => setBatchTagOpen(false)}
          items={batchTagItems}
          entityLabel="stream"
          allTags={allTags}
          isPending={batchTagMutation.isPending}
          onApply={handleBatchTagApply}
        />
      )}

      {publishStream && (
        <PublishModal
          opened={!!publishStream}
          onClose={() => setPublishStream(null)}
          media={publishStream}
        />
      )}

      {encoderStream && (
        <StreamEncoderSetupModal
          opened={!!encoderStream}
          onClose={() => setEncoderStream(null)}
          stream={encoderStream}
          backendMap={backendMap}
        />
      )}

      <CreateStreamModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        liveBackends={liveBackends}
        allowDvr={allowDvr}
        allowEncryptLive={allowEncryptLive}
      />
    </Stack>
  );
}

export function StreamsPage() {
  return <StreamsPageContent />;
}
